import LoadingScreen from "components/LoadingScreen";
import { ModuleContextProvider } from "contexts/ModuleContext";
import DashboardLayoutV3 from "layouts/layout-v3/DashboardLayout";
import CommunitiesRouter from "pages/communities/communities/communities-router";
import InstallationListPage from "pages/communities/installations/installation-list";
import MemberRouter from "pages/communities/installations/member-router";
import { lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";

const Loadable = (Component) => (props) => {
    return (
        <Suspense fallback={<LoadingScreen />}>
            <Component {...props} />
        </Suspense>
    );
}; // dashboards

/**
 * ENERGIA INDIE CUSTOM ROUTES
 */ 
/* Communities Module */
const CommunitiesList = Loadable(lazy(() => import("./pages/communities/communities/communities-list")));
const CommunityInstallationsList = Loadable(lazy(() => import("./pages/communities/installations/installation-list")));
const CommunityinstallationsDetails = Loadable(lazy(() => import("./pages/communities/installations/installation-details")));
const CommunitiesClientsList = Loadable(lazy(() => import("./pages/communities/clients/client-list")));
const CommunityMemberDetails = Loadable(lazy(() => import("./pages/communities/members/CommunityMemberDetails")));
const ClientCommunitiesList = Loadable(lazy(() => import("./pages/communities/communities/ClientCommunitiesList")));
const ClientCommunityDetails = Loadable(lazy(() => import("./pages/communities/communities/ClientCommunityDetails")));

/* Assets Module */
const ConsumptionInstallationList = Loadable(lazy(() => import("./pages/consumption-installation/consumption-installation-list")));
const ConsumptionInstallationDetails = Loadable(lazy(() => import("./pages/consumption-installation/consumption-installation-details")));
const InstallationsList = Loadable(lazy(() => import("./pages/installations/installations-list")));
const InstallationsDetails = Loadable(lazy(() => import("./pages/installations/installations-details")));
const ProductionUnitsList = Loadable(lazy(() => import("./pages/production-units/production-units-list")));
const ProductionUnitsDetails = Loadable(lazy(() => import("./pages/production-units/production-units-details")));
const StorageInstallationList = Loadable(lazy(() => import("./pages/storage-installation/storage-installation-list")));
/* Members Module */
const EntityList = Loadable(lazy(() => import("./pages/core/entities/entity-list")));
const EntityDetails = Loadable(lazy(() => import("./pages/core/entities/entity-details")));
/* Authentication */
const Logout = Loadable(lazy(() => import("./pages/authentication/logout")));
/* Financial */
const CommunitiesInvoicingList = Loadable(lazy(() => import("./pages/invoicing/communities/communities-invoicing-list")));
/* Agents */
const AggregatorsList = Loadable(lazy(() => import("./pages/power-trading/agents/agents-list")));
const AggregatorDetails = Loadable(lazy(() => import("./pages/power-trading/agents/agents-details")));

const AgentListPage = Loadable(lazy(() => import("./pages/core/agents/agent-list")));
const AgentDetailsPage = Loadable(lazy(() => import("./pages/core/agents/agent-details")));
const AgentRegistrationPage = Loadable(lazy(() => import("./pages/core/agents/agent-registration")));
const AgentAccountPage = Loadable(lazy(() => import("./pages/core/agents/agent-account")));
const AgentEntityDetailsPage = Loadable(lazy(() => import("./pages/core/agents/agent-entities-details")));

const UserListPage = Loadable(lazy(() => import("./pages/core/users/user-list-page")));
const FileImportPage = Loadable(lazy(() => import("./pages/core/file-import/file-import-page")));
const UserDetailsPage = Loadable(lazy(() => import("./pages/core/users/user-details-page")));

/* Producers */
const ProducersList = Loadable(lazy(() => import("./pages/power-trading/producers/producer-list")));
const ProducersDetail = Loadable(lazy(() => import("./pages/power-trading/producers/producer-details")));
const ProducersNew = Loadable(lazy(() => import("./pages/power-trading/producers/producer-new")));
/* Contracts */
const ContractsList = Loadable(lazy(() => import("./pages/power-trading/contracts/contract-list")));
const ContractsDetails = Loadable(lazy(() => import("./pages/power-trading/contracts/contract-details")));
const ContractBalanceDetails = Loadable(lazy(() => import("./pages/power-trading/contracts/contract-balance")));
const ContractNewFinDoc = Loadable(lazy(() => import("./pages/power-trading/contracts/contract-findoc-new")));
const ContractNewFinDocStepper = Loadable(lazy(() => import("./pages/power-trading/contracts/contract-findoc-new2")));
const ContractVoidFinDoc = Loadable(lazy(() => import("./pages/power-trading/contracts/contract-findoc-void")));
const ContractEditFinDoc = Loadable(lazy(() => import("./pages/power-trading/contracts/contract-findoc-edit")));
const ContractViewFinDoc = Loadable(lazy(() => import("./pages/power-trading/contracts/contract-findoc-view")));
/* Financial Documents */
const FinancialDocumentView = Loadable(lazy(() => import("./pages/core/financial-documents/view")));
const CommunitiesTariffPlansList = Loadable(lazy(() => import("./pages/communities/tariffs/tariff-plans-list")));
const TariffPlanDetails = Loadable(lazy(() => import("./pages/core/tariffs/tariff-plan-details")));

const TariffModellListPage = Loadable(lazy(() => import("./pages/core/tariffs/tariff-models-list")));
const TariffModelDetailsPage = Loadable(lazy(() => import("./pages/core/tariffs/tariff-model-details")));
const ClientTariffPlanDetailsPage = Loadable(lazy(() => import("./pages/core/tariffs/ClientTariffPlanDetails")));

/* Clients */
const ClientsList = Loadable(lazy(() => import("./pages/power-trading/clients/client-list")));
const ClientsListV2 = Loadable(lazy(() => import("./pages/core/clients/client-list")));
const ClientDetails = Loadable(lazy(() => import("./pages/core/clients/client-details")));
/* Physical Units */
const PhysicalUnitsList = Loadable(lazy(() => import("./pages/core/physical-units/PhysicalUnitsList")));
const AgentPhysicalUnitsList = Loadable(lazy(() => import("./pages/core/physical-units/AgentPhysicalUnitsList")));
const PhysicalUnitDetails = Loadable(lazy(() => import("./pages/core/physical-units/physical-unit-details")));
const ClientPhysicalUnitDetails = Loadable(lazy(() => import("./pages/core/physical-units/ClientPhysicalUnitDetails")));

/* Power Trading */
const TradingFinDocsList = Loadable(lazy(() => import("./pages/power-trading/financial-docs/financial-docs-list")));
const MarketPhysicalUnitsList = Loadable(lazy(() => import("./pages/power-trading/market-units/market-physical-unit-list")));
const MarketPhysicalUnitsDetails = Loadable(lazy(() => import("./pages/power-trading/market-units/market-physical-unit-details")));

/* Offer Units */
const OfferUnitsList = Loadable(lazy(() => import("./pages/power-trading/offering-units/offering-unit-list")));
const OfferUnitsDetails = Loadable(lazy(() => import("./pages/power-trading/offering-units/offering-unit-details")));

/* Offer Area */
const OfferAreasList = Loadable(lazy(() => import("./pages/power-trading/offering-areas/offering-area-list")));
const OfferAresDetails = Loadable(lazy(() => import("./pages/power-trading/offering-areas/offering-area-details")));


/** ***************************************************************************
 *  System Service Markets
 *  ***************************************************************************
 */
const BilateralContractsPage = Loadable(lazy(() => import("./pages/power-trading/markets/bilateral-contracts/bilateral-contracts-page")));
const BilateralContractDetailPage = Loadable(lazy(() => import("./pages/power-trading/markets/bilateral-contracts/bilateral-contract-detail-page")));


/** ***************************************************************************
 *  Energy Markets Route
 *  ***************************************************************************
 */
const DayAheadMarketPage = Loadable(lazy(() => import("./pages/power-trading/markets/market-dayahead-market")));
const IntradayMarketPage = Loadable(lazy(() => import("./pages/power-trading/markets/market-intraday-market")));
const IntraDayContinuosMarketPage = Loadable(lazy(() => import("./pages/power-trading/markets/market-intraday-continuos-market")));
const AFRRBandMarketPage = Loadable(lazy(() => import("./pages/power-trading/markets/market-aFRR-band")));
const MFRRMarketPage = Loadable(lazy(() => import("./pages/power-trading/markets/mfrr/mfrr-page")));

const TradingMarketOfferDetailsPage = Loadable(lazy(() => import("./pages/power-trading/markets/market-offer-details")));

const SettlementsPage = Loadable(lazy(() => import("./pages/power-trading/settlements/settlements-page")));

const UnavailabilitiesPage = Loadable(lazy(() => import("./pages/power-trading/unavailabilities/unavailabilities-page")));

const DataUploadPage = Loadable(lazy(() => import("./pages/power-trading/data-upload/data-upload-list")));
/** ***************************************************************************
 *  Energy Management Routes 
 *  ***************************************************************************
 */

/** Energy Forecasts */
const ForecastTypesListPage = Loadable(lazy(() => import("./pages/core/energy-management/forecasts/forecast-type-list")));
const ForecastTypeDetailsPage = Loadable(lazy(() => import("./pages/core/energy-management/forecasts/forecast-type-details")));
const ForecastTypeInstanceAddPage = Loadable(lazy(() => import("./pages/core/energy-management/forecasts/forecast-type-instance-add")));
const ForecastTypeInstanceDetailsPage = Loadable(lazy(() => import("./pages/core/energy-management/forecasts/forecast-type-instance-details")));
/** ***************************************************************************
 *  User Management Routes 
 *  ***************************************************************************
 */
const UserRegistrationPage = Loadable(lazy(() => import("./pages/core/user/user-registration")));
const UserPasswordResetRequest = Loadable(lazy(() => import("./pages/core/user/user-password-reset-request")));
const UserPasswordReset = Loadable(lazy(() => import("./pages/core/user/user-password-reset")));
const UserRegistrationResend = Loadable(lazy(() => import("./pages/core/user/user-registration-request")));
const UserCompleteRegistration = Loadable(lazy(() => import("./pages/core/user/user-complete-registration")));
/**
 * Reports Routes
 */
const ReportsList = Loadable(lazy(() => import("./pages/core/reports/reports-list")));
const ReportRequest = Loadable(lazy(() => import("./pages/core/reports/new-report-request")));

/** ***************************************************************************
 *  Other Routes 
 *  ***************************************************************************
 */
const TermsAndConditionsPage = Loadable(lazy(() => import("./pages/core/user/terms-and-conditions")));
const PrivacyAndTermsPage = Loadable(lazy(() => import("./pages/core/user/PrivacyAndTermsPage")));
/**
 * TEMPLATE ROUTES
 */
const Login = Loadable(lazy(() => import("./pages/authentication/login")));
const MarketAgentLogin = Loadable(lazy(() => import("./pages/authentication/market-agent-login")));
const Error404 = Loadable(lazy(() => import("./pages/404")));
/** ***************************************************************************
 *  Member Area Routes 
 *  ***************************************************************************
 */
const HomePage = Loadable(lazy(() => import("./pages/dashboards/home")));
const ClientAccountDetails = Loadable(lazy(() => import("./pages/core/entities/ClientAccountDetails")));
const SaftFiles = Loadable(lazy(() => import("./pages/core/entities/entity-saft-files")));

const routes = () => {
    return [
        ...authRoutes,
        {
            path: "terms",
            element: <TermsAndConditionsPage />
        },
        {
            path: "privacy-terms",
            element: <PrivacyAndTermsPage />
        },
        {
            path: "home",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path:"",
                    element: <HomePage />
                }
            ] 
        },
        {
            path: "account",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path:"agent",
                    element: <AgentAccountPage />
                },
                {
                    path:"agent/*",
                    element: <AgentAccountPage />
                },
                {
                    path:"client",
                    element: <ClientAccountDetails />
                },
                {
                    path:"client/*",
                    element: <ClientAccountDetails />
                },
            ] 
        },
        {
            path:"agent",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path: "entities/:id",
                    element: <AgentEntityDetailsPage />
                },
            ]
        },
        {
            path:"saft",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path:"",
                    element: <SaftFiles />
                },
            ] 
        },
        {
            path: "*",
            element: <Error404 />,
        },
        {
            path: "not-found",
            element: <Error404 />,
        },
        {
            path: "admin",
            element: <DashboardLayoutV3 />,
            children: adminRoutes,
        },
        {
            path: "tariff-models",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path: "",
                    element: <TariffModellListPage />
                },
                {
                    path: ":id",
                    element: <TariffModelDetailsPage />
                }
            ]

        },
        {
            path: "communities",
            element: <DashboardLayoutV3 />,
            children: communitiesRoutes,
        },
        {
            path: "aggregators",
            element: <DashboardLayoutV3 />,
            children: aggregatorsRoutes,
        },
        {
            path: "markets",
            element: <DashboardLayoutV3 />,
            children: marketsRoutes,
        },
        {
            path: "reports",
            element: <DashboardLayoutV3 />,
            children: reportsRoutes,
        },
        {
            path: "data",
            element: <DashboardLayoutV3 />,
            children: [{
                path: "",
                element: <DataUploadPage />,
            },],
        },
        {
            path: "producers",
            element: <DashboardLayoutV3 />,
            children: producerRoutes,
        },
        {
            path: "financial-documents",
            element: <DashboardLayoutV3 />,
            children: financialDocumentRoutes,
        },
        {
            path: "clients",
            element: <DashboardLayoutV3 />,
            children: clientRoutes,
        },
        {
            path: "physical-units",
            element: <DashboardLayoutV3 />,
            children: physicalUnitRoutes,
        },
        {
            path:"power-trading",
            element: <DashboardLayoutV3 />,
            children: tradingFindocsRoutes,
        },
        {
            path: "contracts",
            element: <DashboardLayoutV3 />,
            children: contractRoutes,
        },
        {
            path: "tariff-plans",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path: ":id/*",
                    element: <TariffPlanDetails />
                },
            ],
        },
        {
            path: "members",
            element: <DashboardLayoutV3 />,
            children: membersRoutes,
        },
        {
            path: "entities",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path: ":id/*",
                    element: <EntityDetails />
                },
                {
                    path: "",
                    element: <EntityList />
                },
                {
                    path: "add",
                    element: <EntityDetails />
                }
            ],
        },
        {
            path: "consumption-installations",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path: "",
                    element: <ConsumptionInstallationList />
                },
                {
                    path: "add",
                    element: <ConsumptionInstallationDetails />
                },
                {
                    path: ":id",
                    element: <ConsumptionInstallationDetails />
                }
            ]

        },
        {
            path: "installations",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path: "",
                    element: <InstallationsList />
                },
                {
                    path: "add",
                    element: <InstallationsDetails />
                },
                {
                    path: ":id",
                    element: <InstallationsDetails />
                },
            ]

        },
        {
            path: "energy-management",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path: "forecasts",
                    children: forecastRoutes
                },
            ]

        },
        {
            path: "production-units",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path: "",
                    element: <ProductionUnitsList />
                },
                {
                    path: "add",
                    element: <ProductionUnitsDetails />
                },
                {
                    path: ":id",
                    element: <ProductionUnitsDetails />
                },
            ]

        },
        {
            path: "market-units",
            element: <DashboardLayoutV3 />,
            children: tradingUnitRoutes
        },
        {
            path: "offering-units",
            element: <DashboardLayoutV3 />,
            children: offerUnitRoutes
        },
        {
            path: "offering-areas",
            element: <DashboardLayoutV3 />,
            children: offeringAreaRoutes
        },
        {
            path: "storage-installatons",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path: "",
                    element: <StorageInstallationList />
                },
            ]

        },
        {
            path: "invoicing",
            element: <DashboardLayoutV3 />,
            children: [
                {
                    path: "communities/*",
                    element: <CommunitiesInvoicingList />
                },
                {
                    name: "label.menu.powertrading",
                    path: "powertrading/*",
                    element: <TradingFinDocsList />,
                },
            ],
        },
    ];
};

const adminRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                path: "agents/:id/*",
                element: <AgentDetailsPage />
            },
            {
                path: "agents",
                element: <AgentListPage />
            },
            {
                path: "users/:id/*",
                element: <UserDetailsPage />
            },
            {
                path: "users",
                element: <UserListPage />
            },
            {
                path: "file-import/*",
                element: <FileImportPage />
            }

        ]
    }
]
const communitiesRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                path: "",
                element: <CommunitiesList />
            },
            {
                path: "installations/*",
                element: <CommunityInstallationsList />
            },
            {
                path: "installations/production",
                element: <CommunityInstallationsList />
            },
            {
                path: "installations/consumption",
                element: <CommunityInstallationsList />
            },
            {
                path: "/communities/installations/:id/*",
                element: <CommunityinstallationsDetails />
            },
            {
                path: "/communities/:community_id/*",
                element: <CommunitiesRouter />
            },
            {
                path: "/communities/communities/:community_id/*",
                element: <CommunitiesRouter />
            },
            {
                path: "clients/:id/*",
                element: <ClientDetails />
            },
            {
                path: "/communities/clients",
                element: <CommunitiesClientsList />
            },
            {
                path: ":communityId/members/:id/*",
                element: <CommunityMemberDetails />
            },
            {
                path: "client-communities",
                element: <ClientCommunitiesList />
            },
            {
                path: "client-communities/:community_id/*",
                element: <ClientCommunityDetails />
            },
            {
                name: "label.title.invoicingClients",
                path: "client-communities/:community_id/contracts/:id/*",
                element: <ClientTariffPlanDetailsPage />,
            },
        ]
    }
]

const tradingFindocsRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.menu.powerTrading",
                path: "",
                element: <TradingFinDocsList />,
            },
            {
                name: "label.menu.powerTrading",
                path: "*",
                element: <TradingFinDocsList />,
            },
        ]
    }
];

const clientRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.title.invoicingClients",
                path: "",
                element: <ClientsListV2 />,
            },
            {
                name: "label.title.viewContract",
                path: ':clientId/contracts/:id/*',
                element: <ContractsDetails />,
            },
            {
                name: "label.title.invoicingClients",
                path: ":id/*",
                element: <ClientDetails />,
            },
            {
                path: ":id/contracts/:contractId/receipts/balance/:year/:month/*",
                element: <ContractBalanceDetails />
            },
        ]
    }
];

const physicalUnitRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.title.invoicingClients",
                path: "",
                element: <ClientsList />,
            },
            {
                name: "label.title.viewContract",
                path: ':clientId/contracts/:id/*',
                element: <ContractsDetails />,
            },
            {
                path: "client-physical-units",
                element: <PhysicalUnitsList />,
            },
            {
                path: "agent-physical-units",
                element: <AgentPhysicalUnitsList />,
            },
            {
                path: ":physical_unit_id/plans/:id*",
                element: <TariffPlanDetails />,
            },
            {
                name: "label.title.invoicingClients",
                path: ":id/*",
                element: <PhysicalUnitDetails />,
            },
            {
                name: "label.title.invoicingClients",
                path: "client-physical-units/:id/*",
                element: <ClientPhysicalUnitDetails />,
            },
            {
                name: "label.title.invoicingClients",
                path: "client-physical-units/:physical_unit_id/plans/:id/*",
                element: <ClientTariffPlanDetailsPage />,
            },
        ]
    }
];


const reportsRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.title.reports",
                path: "",
                element: <ReportsList />,
            },
            {
                name: "label.title.newReportRequest",
                path: "request",
                element: <ReportRequest />,
            }
        ]
    }
];

const aggregatorsRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.title.aggregators",
                path: "",
                element: <AggregatorsList />,
            },
            {
                name: "label.title.aggregators",
                path: ":id/*",
                element: <AggregatorDetails />,
            },
        ]
    },
];

const marketsRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.title.dailyMarkets",
                path: "dayahead/*",
                element: <DayAheadMarketPage />,
            },
            {
                name: "label.title.intradayMarkets",
                path: "intraday/*",
                element: <IntradayMarketPage />,
            },
            {
                name: "label.title.intradayMarkets",
                path: "intraday-continuos/*",
                element: <IntraDayContinuosMarketPage />,
            },
            {
                name: "market.aFRRBand",
                path: "bafrr/*",
                element: <AFRRBandMarketPage />,
            },
            {
                name: "market.mfrr",
                path: "mfrr/*",
                element: <MFRRMarketPage />,
            },
            {
                name: "market.bilateralContracts",
                path: "ppa/*",
                element: <BilateralContractsPage />,
            },
            {
                name: "market.bilateralContractDetail",
                path: "ppa/detail/:id",
                element: <BilateralContractDetailPage />,
            },
            {
                name: "label.title.marketOfferDetails",
                path: "offers/:id/details",
                element: <TradingMarketOfferDetailsPage />,
            },
            {
                name: "label.title.marketOfferDetails",
                path: "settlements/*",
                element: <SettlementsPage />,
            },
            {
                name: "market.unavailabilities",
                path: "unavailabilities/*",
                element: <UnavailabilitiesPage />,
            },
        ]
    }
]

const financialDocumentRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
             {
                name: "label.title.invoicingClients",
                path: ":findocid/view",
                element: <FinancialDocumentView />,
            },
            {
                name: "label.title.invoicingClients",
                path: ":findocid/void",
                element: <ContractVoidFinDoc />,
            },
        ]
    }
]
const forecastRoutes = [
    {
        path: "types",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.title.forecastTypes",
                path: "",
                element: <ForecastTypesListPage />,
            },
            {
                name: "label.title.forecastTypes",
                path: ":id/instance/add",
                element: <ForecastTypeInstanceAddPage />,
            },
            {
                name: "label.title.forecastTypes",
                path: ":id/locations/:instanceId/",
                element: <ForecastTypeInstanceDetailsPage />,
            },
            {
                name: "label.title.forecastTypes",
                path: ":id/*",
                element: <ForecastTypeDetailsPage />,
            },
        ]
    },
]

const producerRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.title.producers",
                path: "",
                element: <ProducersList />,
            },
            {
                name: "label.title.newProducer",
                path: "add/*",
                element: <ProducersNew />
            },
            {
                name: "label.title.viewContract",
                path: ':producerId/contracts/:id/*',
                element: <ContractsDetails />,
            },
            {
                path: ":producerId/contracts/:contractId/receipts/balance/:year/:month/*",
                element: <ContractBalanceDetails />
            },
            {
                name: "label.title.viewProducer",
                path: ":id/*",
                element: <ProducersDetail />,
            },
        ]
    },
]

const contractRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                path: "powertrading",
                element: <ContractsList />,
                
            },
            {
                path: "communities",
                element: <CommunitiesTariffPlansList />,
                
            },
            {
                path: "communities/:id/*",
                element: <TariffPlanDetails />,
                
            },
            {
                path: ":contractId/receipts/balance/:year/:month/*",
                element: <ContractBalanceDetails />
            },
            {
                path: ':id/financial-document/new',
                element: <ContractNewFinDoc />
            },
            {
                path: ':id/financial-document/guided-new',
                element: <ContractNewFinDocStepper />
            },
            {
                path: ':id/financial-document/:findocid/void',
                element: <ContractVoidFinDoc />
            },
            {
                path: ':id/financial-document/:findocid/edit',
                element: <ContractEditFinDoc />
            },
            {
                path: ':id/financial-document/:findocid/view',
                element: <ContractViewFinDoc />
            },
            {
                path: ":id/*",
                element: <ContractsDetails />
            },
        ]
    },
];

const membersRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                path: "",
                element: <InstallationListPage />
            },
            {
                path: ":id/*",
                element: <MemberRouter />
            },
        ]
    },
]

const tradingUnitRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.title.marketUnits",
                path: "",
                element: <MarketPhysicalUnitsList />,
            },
            {
                name: "label.title.aggregators",
                path: ":id/*",
                element: <MarketPhysicalUnitsDetails />,
            },
        ]
    },
];

const offerUnitRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.title.offerUnits",
                path: "",
                element: <OfferUnitsList />,
            },
            {
                name: "label.title.aggregators",
                path: ":id/*",
                element: <OfferUnitsDetails />,
            }
        ]
    },
];

const offeringAreaRoutes = [
    {
        path: "",
        element: <ModuleContextProvider />,
        children: [
            {
                name: "label.title.offerAreas",
                path: "",
                element: <OfferAreasList />,
            },
            {
                name: "label.title.aggregators",
                path: ":id/*",
                element: <OfferAresDetails />,
            }
        ]
    },
]


const authRoutes = [
    {
        path: "/",
        element: <Navigate to="/home" />,
    },
    {
        path: "login",
        element: <Login />,
    },
    {
        path: "login/agent",
        element: <MarketAgentLogin />
    },
    {
        path: "logout",
        element: <Logout />,
    },
    {
        path: "agent/register/:token",
        element: <AgentRegistrationPage />
    },
    {
        path: "user/register/request",
        element: <UserRegistrationResend />
    },
    {
        path: "user/register/:token",
        element: <UserRegistrationPage />
    },
    {
        path: "user/forget-password/request",
        element: <UserPasswordResetRequest />
    },
    {
        path: "user/forget-password/:token",
        element: <UserPasswordReset />
    },
    {
        path: "user/complete-registration/:token",
        element: <UserCompleteRegistration />
    }
];

export default routes;
